import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Crear cliente de React Query con configuración optimizada
const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Tiempo de stale: 5 minutos
        staleTime: 5 * 60 * 1000,
        // Tiempo de cache: 10 minutos (gcTime en v5)
        gcTime: 10 * 60 * 1000,
        // Reintentar en caso de error
        retry: (failureCount, error: any) => {
          // No reintentar en errores 4xx
          if (error?.status >= 400 && error?.status < 500) {
            return false
          }
          // Reintentar hasta 3 veces
          return failureCount < 3
        },
        // Tiempo entre reintentos
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch en window focus (desactivado en móviles)
        refetchOnWindowFocus: typeof window !== 'undefined' && window.innerWidth > 768,
        // Refetch en reconnect
        refetchOnReconnect: true,
        // Refetch en mount
        refetchOnMount: true,
      },
      mutations: {
        // Reintentar mutations una vez
        retry: 1,
        // No retry en errores 4xx
        retryDelay: 1000,
      },
    },
  })
}

let queryClient: QueryClient | null = null

export const getQueryClient = () => {
  if (!queryClient) {
    queryClient = createQueryClient()
  }
  return queryClient
}

interface QueryProviderProps {
  children: React.ReactNode
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  const queryClientInstance = getQueryClient()

  return (
    <QueryClientProvider client={queryClientInstance}>
      {children}
    </QueryClientProvider>
  )
}

// Hook para invalidar caché programáticamente
export const useInvalidateCache = () => {
  const queryClient = getQueryClient()

  return {
    invalidateQueries: (key: string | string[]) => {
      queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
    },
    refetchQueries: (key: string | string[]) => {
      queryClient.refetchQueries({ queryKey: Array.isArray(key) ? key : [key] })
    },
    clearCache: () => {
      queryClient.clear()
    },
  }
}


